"use client";
import { useState, useRef, useEffect } from "react";
import Link from "next/link";
import Image from "next/image";

import { m, AnimatePresence } from "framer-motion";
import ThemeToggle from "./common/ThemeToggle";
import { usePathname } from "next/navigation";
import { twMerge } from "tailwind-merge";

function Dropdown({
  label,
  items,
  groups,
  open,
  setOpen,
  refEl,
  isActive,
  variant = "desktop",
  closeMenu,
  onOpen,
  pathname,
  openGroups,
  toggleGroup,
}) {
  const isMobile = variant === "mobile";
  const btnClass = isMobile
    ? `flex items-center justify-between font-inter font-medium cursor-pointer text-[14px] ${isActive || open ? "text-[#FF1CF7]" : ""}`
    : `flex items-center gap-1 font-inter font-medium cursor-pointer text-[12px] mobile2:text-[10px] sm:text-[13px] lg:text-[14px] laptop:text-4 3xl:text-[18px] 4xl:text-5 ${isActive || open ? "text-[#FF1CF7]" : ""}`;

  const renderItem = (it, i) => {
    const active = pathname === it.href;
    const isExternal = /^https?:\/\//.test(it.href);
    return (
      <li key={i}>
        <Link
          href={it.href}
          {...(isExternal && { target: "_blank", rel: "noopener noreferrer" })}
          onClick={() => {
            setOpen(false);
            if (isMobile && typeof closeMenu === "function") closeMenu();
          }}
          className={
            isMobile
              ? `text-[13px] block font-inter font-normal ${active ? "text-[#FF1CF7] font-semibold whitespace-nowrap" : ""}`
              : `block px-4 py-2 text-[13px] lg:text-[14px] hover:bg-[#FFFFFF] dark:hover:bg-[#121212] whitespace-nowrap transition-colors ${active ? "text-[#FF1CF7] font-semibold" : ""}`
          }
        >
          {it.label}
        </Link>
      </li>
    );
  };

  return (
    <li className={isMobile ? "flex flex-col" : "relative"} ref={refEl}>
      <button
        onClick={() => {
          const next = !open;
          setOpen(next);
          if (next && typeof onOpen === "function") onOpen();
        }}
        className={btnClass}
      >
        {label}
        <svg
          className={`w-4 h-4 transition-transform ${open ? "rotate-180" : ""}`}
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M19 9l-7 7-7-7"
          />
        </svg>
      </button>

      <AnimatePresence>
        {open &&
          (isMobile ? (
            <ul className="ml-1 mt-2 space-y-3">
              {groups
                ? groups.map((group) => {
                  const groupOpen = openGroups[group.key];
                  const groupActive = group.items.some(
                    (it) => it.href && pathname.startsWith(it.href),
                  );
                  return (
                    <li key={group.key} className="flex flex-col">
                      <button
                        onClick={() => toggleGroup(group.key)}
                        className={`flex items-center justify-between w-full font-inter font-semibold text-[13px] cursor-pointer ${groupActive && "text-[#FF1CF7]"}`}
                      >
                        {group.label}
                        <svg
                          className={`w-3.5 h-3.5 transition-transform ${groupOpen ? "rotate-180" : ""}`}
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M19 9l-7 7-7-7"
                          />
                        </svg>
                      </button>
                      {groupOpen && (
                        <ul className="ml-2 mt-2 space-y-2">
                          {group.items.map((it, i) => renderItem(it, i))}
                        </ul>
                      )}
                    </li>
                  );
                })
                : items.map((it, i) => renderItem(it, i))}
            </ul>
          ) : (
            <m.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -10 }}
              transition={{ duration: 0.2 }}
              className="absolute left-0 top-full mt-2 bg-[#F7F2FF] dark:bg-[#020409] min-w-[250px] max-h-[70vh] overflow-y-auto rounded-lg z-50"
              style={{
                boxShadow: document.documentElement.classList.contains("dark")
                  ? "0px 2px 4px 2px rgba(135, 135, 135, 0.25)"
                  : "0px 4px 4px 0px rgba(0, 0, 0, 0.25)",
              }}
            >
              <ul className="py-2">
                {groups
                  ? groups.map((group, gi) => (
                    <li
                      key={group.key}
                      className={
                        gi > 0
                          ? "mt-1 pt-1 border-t border-black/10 dark:border-white/10"
                          : ""
                      }
                    >
                      <p className="px-4 pt-2 pb-1 font-inter font-semibold text-[11px] lg:text-[12px] uppercase tracking-wider opacity-50">
                        {group.label}
                      </p>
                      <ul>
                        {group.items.map((it, i) => renderItem(it, i))}
                      </ul>
                    </li>
                  ))
                  : items.map((it, i) => renderItem(it, i))}
              </ul>
            </m.div>
          ))}
      </AnimatePresence>
    </li>
  );
}

export default function Navbar({ className }) {
  const [open, setIsOpen] = useState(false);
  const [servicesOpen, setServicesOpen] = useState(false);
  const [projectsOpen, setProjectsOpen] = useState(false);
  const [openGroups, setOpenGroups] = useState({ core: false, other: false });
  const [mounted, setMounted] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const pathname = usePathname();

  useEffect(() => {
    setMounted(true);
  }, []);

  const enableBlog = mounted
    ? (process.env.NEXT_PUBLIC_ENABLE_BLOG || "").toLowerCase() === "true"
    : false;
  const enableChat = mounted
    ? (process.env.NEXT_PUBLIC_ENABLE_CHAT || "").toLowerCase() === "true"
    : false;

  const menuRef = useRef(null);
  const servicesRef = useRef(null);
  const projectsRef = useRef(null);

  const toggleGroup = (key) =>
    setOpenGroups((prev) => ({ ...prev, [key]: !prev[key] }));

  const navItems = [
    { label: "Home", href: "/", key: "home" },
    {
      label: "Services",
      href: "/services",
      key: "services",
      hasDropdown: true,
    },
    {
      label: "Projects & Products",
      href: "/projects",
      key: "projects",
      hasDropdown: true,
    },
    { label: "Blog", href: "/blog", key: "blog" },
    { label: "Success Stories", href: "/successStories", key: "successStories" },
    { label: "About Us", href: "/about", key: "aboutUs" },
    { label: "Contact Us", href: "/contact", key: "contactUs" },
    { label: "Careers", href: "/careers", key: "careers" },
  ];

  const visibleNavItems = navItems.filter(
    (item) => !(item.key === "blog" && !enableBlog),
  );

  const serviceGroups = [
    {
      key: "core",
      label: "Core Services",
      items: [
        { label: "AI Assistant Solutions", href: "#" },
        { label: "Business Intelligence Systems", href: "#" },
        { label: "Data Warehouse Solutions", href: "#" },
        { label: "AI Workflow Automation", href: "#" },
      ],
    },
    {
      key: "other",
      label: "Other Services",
      items: [
        { label: "MVP Development", href: "/mvp" },
        { label: "Web Development", href: "/webApp" },
        { label: "Mobile App Development", href: "/mobileApp" },
        { label: "UI/UX Design", href: "/uiux" },
        { label: "Custom Software Development", href: "/software-development" },
        { label: "Cloud & DevOps", href: "/cloud" },
        { label: "Machine Learning Solutions", href: "/data&ai" },
        { label: "System Integrations", href: "/software-integration" },
      ],
    },
  ];

  const serviceItems = serviceGroups.flatMap((g) => g.items);

  const projectItems = [
    { label: "Our Work", href: "/ourWork" },
    { label: "Sample Projects", href: "/projects" },
    { label: "AI Nexus", href: "https://ai-nexus.icieos.com/" },
  ];

  const isOnServicePage = serviceItems.some((service) =>
    pathname.startsWith(service.href),
  );

  const isOnProjectPage =
    pathname.startsWith("/projects") || pathname.startsWith("/ourWork");

  useEffect(() => {
    function handleClickOutside(e) {
      if (menuRef.current && !menuRef.current.contains(e.target)) {
        setIsOpen(false);
      }
      if (servicesRef.current && !servicesRef.current.contains(e.target)) {
        setServicesOpen(false);
      }
      if (projectsRef.current && !projectsRef.current.contains(e.target)) {
        setProjectsOpen(false);
      }
    }

    if (open || servicesOpen || projectsOpen) {
      document.addEventListener("click", handleClickOutside);
    }
    return () => document.removeEventListener("click", handleClickOutside);
  }, [open, servicesOpen, projectsOpen]);

  useEffect(() => {
    if (typeof window === "undefined") return;
    const isMobile = window.innerWidth < 580;
    if (isOnServicePage && isMobile) {
      setServicesOpen(true);
    } else if (isMobile) {
      setServicesOpen(false);
    }

    // Open whichever group holds the current page
    if (isOnServicePage) {
      setOpenGroups({
        core: serviceGroups[0].items.some((s) => pathname.startsWith(s.href)),
        other: serviceGroups[1].items.some((s) => pathname.startsWith(s.href)),
      });
    }

    if (isOnProjectPage && isMobile) {
      setProjectsOpen(true);
    } else if (isMobile) {
      setProjectsOpen(false);
    }
  }, [pathname]);

  useEffect(() => {
    const onScroll = () => {
      setScrolled(window.scrollY > 20);
    };

    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <m.nav
      initial={{ y: -100, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.6, ease: "easeOut" }}
      className={twMerge(
        "fixed top-0 w-full z-9999",
        scrolled ? "backdrop-blur-md bg-background/70" : "bg-transparent",
        className,
      )}
    >
      <div className="px-5 tablet:px-10 laptop:px-25 5xl:px-0">
        <div className="flex justify-between items-center bg-transparent w-full max-w-[2000px] mx-auto mt-5">
          <Link href="/" className="relative z-50" suppressHydrationWarning>
            {/* Light mode logo - hidden in dark mode */}
            <Image
              src="../assets/logo/LOGO.svg"
              alt="ICIEOS Logo"
              width={147}
              height={43}
              className="w-[92px] sm:w-[98px] xl:w-[127px] transition-opacity duration-200 dark:hidden"
              unoptimized
            />
            {/* Dark mode logo - hidden in light mode */}
            <Image
              src="../assets/logo/LOGO-dark.svg"
              alt="ICIEOS Logo"
              width={147}
              height={43}
              className="w-[92px] sm:w-[98px] xl:w-[127px] transition-opacity duration-200 hidden dark:block"
              unoptimized
            />
          </Link>
          <div className="flex items-center gap-x-3 mobile:gap-x-5 md:gap-x-2 laptop:gap-x-[45px] 3xl:gap-x-[55px] 4xl:gap-x-[65px]">
            <ul
              className="hidden tablet:flex items-center space-x-3 tablet:space-x-2 lg:space-x-5 laptop:space-x-[45px] 3xl:space-x-[55px] 4xl:space-x-[65px] text-paragraph font-inter font-medium"
              suppressHydrationWarning
            >
              {visibleNavItems.map((item) => {
                let isActive = false;

                if (item.hasDropdown) {
                  if (item.key === "services") {
                    isActive =
                      isOnServicePage || pathname.startsWith("/services");
                  } else if (item.key === "projects") {
                    isActive =
                      isOnProjectPage || pathname.startsWith("/projects");
                  }
                } else if (item.href === "/") {
                  const otherRoutes = navItems
                    .filter((n) => n.href !== "/" && !n.hasDropdown)
                    .map((n) => n.href);

                  const matchedOther = otherRoutes.some((route) =>
                    pathname.startsWith(route),
                  );

                  isActive =
                    !matchedOther && !isOnServicePage && !isOnProjectPage;
                } else {
                  isActive = pathname.startsWith(item.href);
                }

                if (item.hasDropdown) {
                  const items =
                    item.key === "services" ? serviceItems : projectItems;
                  const groups =
                    item.key === "services" ? serviceGroups : null;
                  const openState =
                    item.key === "services" ? servicesOpen : projectsOpen;
                  const setOpenState =
                    item.key === "services" ? setServicesOpen : setProjectsOpen;
                  const refEl =
                    item.key === "services" ? servicesRef : projectsRef;

                  return (
                    <Dropdown
                      key={item.key}
                      label={item.label}
                      items={items}
                      groups={groups}
                      open={openState}
                      setOpen={setOpenState}
                      refEl={refEl}
                      isActive={isActive}
                      variant="desktop"
                      pathname={pathname}
                      openGroups={openGroups}
                      toggleGroup={toggleGroup}
                      onOpen={
                        item.key === "services"
                          ? () => setProjectsOpen(false)
                          : () => setServicesOpen(false)
                      }
                    />
                  );
                }

                return (
                  <li
                    key={item.key}
                    className={`flex items-center ${isActive ? "text-[#FF1CF7]" : ""}`}
                  >
                    <Link
                      href={item.href}
                      className="font-inter font-normal text-[12px] mobile2:text-[11px] sm:text-[13px] lg:text-[14px] laptop:text-4 3xl:text-[18px] 4xl:text-5"
                    >
                      {item.label}
                    </Link>
                  </li>
                );
              })}
            </ul>

            <div className="flex items-center gap-x-3 mobile:gap-x-5 md:gap-x-3 tablet:gap-x-3 lg:gap-x-5 3xl:gap-x-[55px] 4xl:gap-x-[65px] z-50">
              <ThemeToggle />

              {enableChat && (
                <Link href="/chat">
                  <button className="relative flex items-center justify-center border border-[#DF27DF] rounded-[5px] font-manrope font-bold text-[11px] lg:text-[12px] laptop:text-[13px] 3xl:text-[15px] 4xl:text-[17px] leading-6 bg-clip-text text-transparent bg-linear-to-r from-[#3950D1] to-[#BA00BA] cursor-pointer max-h-7 xl:max-h-[31px] py-1 px-1.5 pl-7 z-100">
                    <Image
                      src="/assets/chatBot/ellipse-avatar.svg"
                      alt="iconBg"
                      width={19}
                      height={19}
                      className="absolute left-1.5 z-0"
                    />
                    <Image
                      src="/assets/chatBot/chatBot.svg"
                      alt="icon"
                      width={13}
                      height={12}
                      className="absolute left-[9px] z-50"
                    />
                    ICIEOS AI
                  </button>
                </Link>
              )}

              {/* <button className="hidden md:flex items-center justify-center border border-[#DF27DF] rounded-[5px] text-[#DF27DF] font-inter font-medium text-[11px] lg:text-[12px] laptop:text-[13px] 3xl:text-[15px] 4xl:text-[17px] cursor-pointer transition-colors max-h-7 xl:max-h-[31px] px-2 xl:px-[18px] py-2">
                Sign In
              </button> */}
            </div>

            {/* Hamberger icon */}
            <Image
              src="/assets/home/hamburger-menu.svg"
              alt="hamburgermenu"
              width={20}
              height={20}
              className="flex tablet:hidden cursor-pointer z-50"
              onClick={() => setIsOpen(!open)}
            />
          </div>
        </div>
      </div>

      {open && (
        <>
          {/* blur background  */}
          <div
            className="fixed inset-0 bg-black/30 backdrop-blur-sm z-40 tablet:hidden"
            onClick={() => setIsOpen(false)}
          ></div>

          {/* mobile screen menu */}
          <div
            ref={menuRef}
            className="absolute right-5 top-9 flex flex-col tablet:hidden bg-[#F7F2FF] dark:bg-[#020409] rounded-xl w-[230px] max-h-[80vh] overflow-y-auto shadow-[0px_4px_4px_0px_rgba(0,0,0,0.25)] dark:shadow-[0px_2px_4px_2px_rgba(135,135,135,0.25)] z-50"
          >
            <div className="p-4">
              <div className="flex flex-col space-y-5" suppressHydrationWarning>
                {visibleNavItems.map((item) => {
                  let isActive = false;

                  if (item.hasDropdown) {
                    if (item.key === "services") {
                      isActive =
                        isOnServicePage || pathname.startsWith("/services");
                    } else if (item.key === "projects") {
                      isActive =
                        isOnProjectPage || pathname.startsWith("/projects");
                    }
                  } else if (item.href === "/") {
                    const otherRoutes = navItems
                      .filter((n) => n.href !== "/" && !n.hasDropdown)
                      .map((n) => n.href);

                    const matchedOther = otherRoutes.some((route) =>
                      pathname.startsWith(route),
                    );

                    isActive =
                      !matchedOther && !isOnServicePage && !isOnProjectPage;
                  } else {
                    isActive = pathname.startsWith(item.href);
                  }

                  if (item.hasDropdown) {
                    const items =
                      item.key === "services" ? serviceItems : projectItems;
                    const groups =
                      item.key === "services" ? serviceGroups : null;
                    const openState =
                      item.key === "services" ? servicesOpen : projectsOpen;
                    const setOpenState =
                      item.key === "services"
                        ? setServicesOpen
                        : setProjectsOpen;
                    const refEl =
                      item.key === "services" ? servicesRef : projectsRef;

                    return (
                      <Dropdown
                        key={item.key}
                        label={item.label}
                        items={items}
                        groups={groups}
                        open={openState}
                        setOpen={setOpenState}
                        refEl={refEl}
                        isActive={isActive}
                        variant="mobile"
                        closeMenu={() => setIsOpen(false)}
                        pathname={pathname}
                        openGroups={openGroups}
                        toggleGroup={toggleGroup}
                      />
                    );
                  }

                  return (
                    <li
                      key={item.key}
                      className={`flex items-center ${isActive ? "text-[#FF1CF7]" : ""}`}
                    >
                      <Link
                        href={item.href}
                        onClick={() => setIsOpen(!open)}
                        className="font-inter font-medium text-sm"
                      >
                        {item.label}
                      </Link>
                    </li>
                  );
                })}
              </div>
              {/* <div className="flex w-full justify-center">
                <button className="flex border border-[#FF1CF7] rounded-md text-[#FF1CF7] text-[13px] justify-center items-center cursor-pointer w-full py-0.5 mt-4">
                  Sign In
                </button>
              </div> */}
            </div>
          </div>
        </>
      )}
    </m.nav>
  );
}
